import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Hourglass } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { tasks as tasksApi } from '@/lib/api/endpoints';
import type { TaskSummary } from '@/lib/api/types';
import { useT, type MessageKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/** Past this, waiting has become forgetting. */
const NAGGING_DAYS = 7;

/**
 * What you are waiting on other people for, on the screen you open every day.
 *
 * Handing work over is not the same as being rid of it: the task leaves your
 * list, and with it every reminder that it exists. "Assigned by me" already
 * held these rows, but nobody opens a screen to be reminded of something they
 * have forgotten — that is what forgetting means. So it comes to Today.
 *
 * Collapsed by default and silent when empty, because it is somebody else's
 * turn: it earns space by being short, and by saying *how long* rather than
 * merely *that*. A week is where a handover starts looking like a dropped
 * ball, and the row says so.
 */
export function WaitingBand({ onOpen }: { onOpen: (taskId: string) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['tasks', 'assigned-by-me', 'waiting'],
    queryFn: () => tasksApi.assignedByMe({ limit: 50 }),
  });

  const waiting = (data?.items ?? [])
    .filter((task) => task.status !== 'COMPLETED' && task.delegated_at)
    .sort((a, b) => (a.delegated_at ?? '').localeCompare(b.delegated_at ?? ''));

  if (waiting.length === 0) return null;

  const stale = waiting.filter((task) => daysWaiting(task) >= NAGGING_DAYS).length;

  return (
    <section className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        className="flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-left hover:bg-surface-hover"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-content-subtle" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-content-subtle" aria-hidden />
        )}
        <Hourglass className="h-3.5 w-3.5 shrink-0 text-content-subtle" aria-hidden />
        <span className="text-sm font-medium text-content">{t('waiting.title')}</span>
        <span className="text-xs tabular-nums text-content-subtle">
          {waiting.length}
        </span>
        {/* The only part that asks for attention: a week without a word. */}
        {stale > 0 ? (
          <span className="rounded-full bg-amber/10 px-2 py-0.5 text-[11px] font-medium text-amber">
            {t('waiting.stale', { count: stale })}
          </span>
        ) : null}
      </button>

      {open ? (
        <ul className="mt-1 space-y-0.5 pl-6">
          {waiting.map((task) => {
            const days = daysWaiting(task);
            return (
              <li key={task.id}>
                <button
                  type="button"
                  onClick={() => onOpen(task.id)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-surface-hover"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-content">
                    {task.title}
                  </span>
                  {/* What you are waiting for, not merely that you are:
                      "Petr, 4 days" and "Petr to approve it, 4 days" call for
                      different follow-ups. */}
                  {task.handover_kind ? (
                    <span className="shrink-0 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-accent">
                      {t(`handover.kind.${task.handover_kind}` as MessageKey)}
                    </span>
                  ) : null}
                  <span className="shrink-0 text-xs text-content-muted">
                    {task.assignee?.display_name ?? ''}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 text-xs tabular-nums',
                      days >= NAGGING_DAYS ? 'text-amber' : 'text-content-subtle',
                    )}
                  >
                    {days === 0
                      ? t('waiting.today')
                      : t('waiting.days', { count: days })}
                  </span>
                </button>
              </li>
            );
          })}
          <li className="pt-1">
            <Link
              to="/assigned-by-me"
              className="px-2 text-xs text-content-muted hover:text-content"
            >
              {t('waiting.seeAll')}
            </Link>
          </li>
        </ul>
      ) : null}
    </section>
  );
}

function daysWaiting(task: TaskSummary): number {
  if (!task.delegated_at) return 0;
  const since = new Date(task.delegated_at).getTime();
  return Math.max(0, Math.floor((Date.now() - since) / 86_400_000));
}
